import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { Pressable } from "react-native";
import { Checkbox, List, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { CustomListSubheader } from "../CustomListSubheader";
import {
	getFilterIcon,
	getSessionItemsNavFilters,
	getStatusMeta,
} from "./helpers";
import type { SessionItemsNavFilterValue, SessionItemsNavItem } from "./types";

type SessionItemsNavHeaderProps = {
	title: string;
	onClose?: () => void;
	showClose?: boolean;
	compact?: boolean;
};

export const SessionItemsNavHeader = ({
	title,
	onClose,
	showClose = true,
	compact = false,
}: SessionItemsNavHeaderProps) => {
	const theme = useAppTheme();

	return (
		<Box
			paddingHorizontal={24}
			paddingTop={compact ? 8 : 24}
			paddingBottom={8}
			backgroundColor={theme.colors.surface}
		>
			<Box flexDirection="row" alignItems="center" gap={8}>
				<Box flex={1}>
					<Text variant="titleLarge">{title}</Text>
				</Box>
				{showClose && onClose && (
					<Pressable onPress={onClose} style={{ marginLeft: "auto" }}>
						<Icon
							name={"close"}
							size={24}
							color={theme.colors.onSurfaceVariant}
						/>
					</Pressable>
				)}
			</Box>
		</Box>
	);
};

type SessionItemsNavFiltersSectionProps = {
	selectedFilters: SessionItemsNavFilterValue[];
	filters?: SessionItemsNavFilterValue[];
	onToggleFilter: (value: SessionItemsNavFilterValue) => void;
};

export const SessionItemsNavFiltersSection = ({
	selectedFilters,
	filters,
	onToggleFilter,
}: SessionItemsNavFiltersSectionProps) => {
	const enabledFilters = getSessionItemsNavFilters(filters);

	if (enabledFilters.length === 0) {
		return null;
	}

	return (
		<>
			<SessionItemsNavSectionTitle title="Filters" />
			{enabledFilters.map((filter) => (
				<SessionItemsNavFilterRow
					key={filter.value}
					filter={filter}
					selected={selectedFilters.includes(filter.value)}
					onToggle={onToggleFilter}
				/>
			))}
		</>
	);
};

export const SessionItemsNavItemsTitle = ({
	itemCount,
}: {
	itemCount: number;
}) => <SessionItemsNavSectionTitle title={`Questions (${itemCount})`} />;

const SessionItemsNavSectionTitle = ({ title }: { title: string }) => {
	return (
		<Box paddingHorizontal={8}>
			<CustomListSubheader>{title}</CustomListSubheader>
		</Box>
	);
};

type SessionItemsNavFilterRowProps = {
	filter: { value: SessionItemsNavFilterValue; label: string };
	selected: boolean;
	onToggle: (value: SessionItemsNavFilterValue) => void;
};

const SessionItemsNavFilterRow = ({
	filter,
	selected,
	onToggle,
}: SessionItemsNavFilterRowProps) => {
	const theme = useAppTheme();
	const iconColor = (() => {
		switch (filter.value) {
			case "correct":
			case "answered":
				return theme.colors.success.main;
			case "incorrect":
				return theme.colors.error;
			case "marked":
				return theme.palette.error.main;
			default:
				return theme.colors.onSurfaceVariant;
		}
	})();

	return (
		<List.Item
			title={filter.label}
			style={{
				paddingVertical: 0,
			}}
			onPress={() => onToggle(filter.value)}
			accessibilityRole="checkbox"
			accessibilityState={{ checked: selected }}
			left={(props) => (
				<List.Icon
					{...props}
					style={{
						...props.style,
						paddingLeft: 4,
					}}
					icon={getFilterIcon(filter.value)}
					color={iconColor}
				/>
			)}
			right={(props) => (
				<List.Icon
					{...props}
					style={{
						...props.style,
						marginRight: -4,
					}}
					icon={() => (
						<Checkbox
							status={selected ? "checked" : "unchecked"}
							color={theme.colors.secondary}
							uncheckedColor={theme.colors.onSurfaceVariant}
						/>
					)}
				/>
			)}
		/>
	);
};

type SessionItemsNavRowProps = {
	item: SessionItemsNavItem;
	index: number;
	isActive: boolean;
	onPress: (item: SessionItemsNavItem, index: number) => void;
};

export const SessionItemsNavRow = ({
	item,
	index,
	isActive,
	onPress,
}: SessionItemsNavRowProps) => {
	const theme = useAppTheme();
	const statusMeta = getStatusMeta(item.status, theme);

	return (
		<List.Item
			title={item.label}
			titleNumberOfLines={1}
			style={{
				paddingVertical: 8,
			}}
			onPress={() => onPress(item, index)}
			accessibilityRole="button"
			accessibilityState={{ selected: isActive }}
			accessibilityLabel={
				item.accessibilityLabel ?? `${item.label}, ${statusMeta.label}`
			}
			left={(props) => (
				<List.Icon
					{...props}
					style={{
						...props.style,
						paddingLeft: 4,
					}}
					icon={statusMeta.icon}
					color={statusMeta.color}
				/>
			)}
			right={(props) =>
				item.isMarked ? (
					<List.Icon {...props} icon="flag" color={theme.palette.error.main} />
				) : null
			}
		/>
	);
};
